import { useEffect, useState } from 'react';
import {
  fetchGoogleAdsCustomers,
  fetchGoogleAdsMerchantAccounts,
  saveGoogleAdsCustomers,
  type GoogleAdsCustomer,
  type MerchantAccount,
  type SelectedGoogleAdsCustomer,
} from '../../api/client';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';

interface GoogleAdsAccountSelectorProps {
  sessionId: string;
  onSaved: (count: number) => void;
  onClose: () => void;
}

const STEP_TITLES = ['Select Ad Accounts', 'Link Merchant Center'];

/**
 * Format customer ID with hyphens for display (e.g. 1234567890 → 123-456-7890).
 */
const formatCustomerId = (id: string) => {
  const digits = id.replace(/\D/g, '');
  if (digits.length === 10) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return id;
};

/** Map account status to a badge variant and label. */
const getStatusBadge = (status?: string) => {
  switch (status) {
    case 'ENABLED':
      return { variant: 'secondary' as const, label: 'Active' };
    case 'PAUSED':
      return { variant: 'secondary' as const, label: 'Paused' };
    case 'SUSPENDED':
      return { variant: 'destructive' as const, label: 'Suspended' };
    case 'CANCELLED':
    case 'CANCELED':
      return { variant: 'destructive' as const, label: 'Cancelled' };
    case 'CLOSED':
      return { variant: 'secondary' as const, label: 'Closed' };
    default:
      return null;
  }
};

/**
 * Google Ads connect wizard (mirrors mvp's GoogleAdsAccountSelector):
 * step 1 — pick ad accounts from the flat customer list grouped under their
 * manager (MCC) cards; step 2 — optionally link a Merchant Center account to
 * each selected customer (Skip connects without links).
 */
export default function GoogleAdsAccountSelector({
  sessionId, onSaved, onClose,
}: GoogleAdsAccountSelectorProps) {
  const [currentStep, setCurrentStep] = useState(1);

  // Step 1: Customers
  const [customers, setCustomers] = useState<GoogleAdsCustomer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState<string[]>([]);
  const [fetchError, setFetchError] = useState<string | null>(null);

  // Step 2: Merchant Center
  const [loadingMerchants, setLoadingMerchants] = useState(true);
  const [merchantAccounts, setMerchantAccounts] = useState<MerchantAccount[]>([]);
  const [merchantScopeError, setMerchantScopeError] = useState(false);
  const [merchantCenterIds, setMerchantCenterIds] = useState<Record<string, string>>({});

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    let cancelled = false;
    Promise.all([
      fetchGoogleAdsCustomers(sessionId),
      fetchGoogleAdsMerchantAccounts(sessionId).catch(() => ({ accounts: [] })),
    ])
      .then(([customersResp, merchant]) => {
        if (cancelled) return;
        setCustomers(customersResp.customers || []);
        setMerchantAccounts(merchant.accounts || []);
        setMerchantScopeError(Boolean((merchant as { scopeError?: boolean }).scopeError));
        setLoadingCustomers(false);
        setLoadingMerchants(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setFetchError(
          err instanceof Error ? err.message : 'Failed to load accounts. Please try again.',
        );
        setLoadingCustomers(false);
        setLoadingMerchants(false);
      });
    return () => { cancelled = true; };
  }, [sessionId]);

  // --- Customer handlers ---
  const handleCustomerToggle = (customerId: string) => {
    setSelectedCustomerIds((prev) =>
      prev.includes(customerId)
        ? prev.filter((id) => id !== customerId)
        : [...prev, customerId],
    );
  };

  const nonManagerCustomers = customers.filter((c) => !c.isManager);
  const managerCustomers = customers.filter((c) => c.isManager);

  // Group non-manager customers by parent MCC for tree display
  const topLevelCustomers = nonManagerCustomers.filter((c) => !c.parentCustomerId);
  const childCustomersByParent = new Map<string, GoogleAdsCustomer[]>();
  for (const customer of nonManagerCustomers) {
    if (customer.parentCustomerId) {
      const children = childCustomersByParent.get(customer.parentCustomerId) || [];
      children.push(customer);
      childCustomersByParent.set(customer.parentCustomerId, children);
    }
  }

  const handleSelectAllCustomers = () => {
    if (selectedCustomerIds.length === nonManagerCustomers.length) {
      setSelectedCustomerIds([]);
    } else {
      setSelectedCustomerIds(nonManagerCustomers.map((c) => c.customerId));
    }
  };

  // --- Build save payload ---
  const buildSelectedCustomers = (includeMerchant: boolean): SelectedGoogleAdsCustomer[] =>
    selectedCustomerIds.map((id) => {
      const customer = nonManagerCustomers.find((c) => c.customerId === id);
      const parentMcc = customer?.parentCustomerId
        ? managerCustomers.find((m) => m.customerId === customer.parentCustomerId)
        : null;
      return {
        customer_id: id,
        customer_name: customer?.customerName || id,
        is_manager: false,
        parent_customer_id: customer?.parentCustomerId || null,
        is_managed: !!parentMcc,
        login_customer_id: parentMcc?.customerId ?? null,
        login_customer_name: parentMcc?.customerName ?? null,
        merchant_center_id: includeMerchant ? merchantCenterIds[id] || null : null,
        currency_code: customer?.currencyCode ?? null,
        time_zone: customer?.timeZone ?? null,
      };
    });

  // --- Navigation ---
  const handleNext = () => {
    if (currentStep === 1) setCurrentStep(2);
  };

  const handleBack = () => {
    if (currentStep === 2) setCurrentStep(1);
  };

  const save = async (includeMerchant: boolean) => {
    setSaving(true);
    setSaveError('');
    try {
      const result = await saveGoogleAdsCustomers({
        session_id: sessionId,
        selected_customers: buildSelectedCustomers(includeMerchant),
      });
      onSaved(result.count);
      onClose();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save Google Ads accounts');
      setSaving(false);
    }
  };

  const handleConfirm = () => void save(true);
  const handleSkipMerchantCenter = () => void save(false);

  const canNext = () => {
    if (currentStep === 1) return selectedCustomerIds.length > 0;
    return true;
  };

  const getSelectionCount = () => {
    if (currentStep === 1) {
      return `${selectedCustomerIds.length} account${selectedCustomerIds.length !== 1 ? 's' : ''} selected`;
    }
    const filledCount = Object.values(merchantCenterIds).filter((v) => v.trim()).length;
    return `${filledCount} of ${selectedCustomerIds.length} linked`;
  };

  /** Look up parent MCC name by its customer ID. */
  const getParentMccName = (parentId?: string | null) => {
    if (!parentId) return null;
    const parent = managerCustomers.find((m) => m.customerId === parentId);
    return parent?.customerName ?? null;
  };

  /** Render a single customer row (checkbox + name + ID + status badge). */
  const renderCustomerRow = (customer: GoogleAdsCustomer, indent = false) => {
    const statusBadge = getStatusBadge(customer.accountStatus);
    const parentName = !indent ? getParentMccName(customer.parentCustomerId) : null;
    const isSelected = selectedCustomerIds.includes(customer.customerId);

    return (
      <label
        key={customer.customerId}
        className={cn(
          'flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors',
          indent && 'ml-6',
          isSelected ? 'border-primary bg-primary/5' : 'border-border hover:bg-muted/50',
        )}
      >
        <input
          type="checkbox"
          checked={isSelected}
          onChange={() => handleCustomerToggle(customer.customerId)}
          className="size-4 shrink-0 accent-primary"
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-sm font-medium">{customer.customerName}</p>
            {statusBadge && statusBadge.label !== 'Active' && (
              <Badge variant={statusBadge.variant} className="px-1.5 py-0 text-[10px]">
                {statusBadge.label}
              </Badge>
            )}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span>ID: {formatCustomerId(customer.customerId)}</span>
            {parentName && (
              <>
                <span>·</span>
                <span className="truncate">via {parentName}</span>
              </>
            )}
          </div>
        </div>
      </label>
    );
  };

  /**
   * Merchant Center link card for a selected customer. mvp auto-discovers the
   * link read-only; our backend returns the merchant accounts available to the
   * Google user, so the card offers a picker instead.
   */
  const renderMerchantLink = (customerId: string) => {
    const customer = nonManagerCustomers.find((c) => c.customerId === customerId);
    return (
      <div key={customerId} className="rounded-lg border border-border p-3">
        <p className="mb-1 text-sm font-medium">{customer?.customerName || customerId}</p>
        <p className="mb-2 text-xs text-muted-foreground">
          Account ID: {formatCustomerId(customerId)}
        </p>
        {merchantAccounts.length > 0 ? (
          <select
            value={merchantCenterIds[customerId] || ''}
            onChange={(e) =>
              setMerchantCenterIds((prev) => {
                const next = { ...prev };
                if (e.target.value) next[customerId] = e.target.value;
                else delete next[customerId];
                return next;
              })
            }
            className="w-full rounded-md border border-border bg-card px-2 py-1.5 text-sm"
          >
            <option value="">No Merchant Center</option>
            {merchantAccounts.map((m) => (
              <option key={m.accountId} value={m.accountId}>
                {m.accountName} ({m.accountId})
              </option>
            ))}
          </select>
        ) : (
          <p className="text-sm text-muted-foreground">No Merchant Center linked in Google Ads.</p>
        )}
      </div>
    );
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !saving) onClose(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <img
              src="/logos/google_ads_logo.svg"
              alt="Google Ads"
              className="size-8 shrink-0 rounded object-contain"
            />
            <div>
              <DialogTitle>{STEP_TITLES[currentStep - 1]}</DialogTitle>
              <DialogDescription>
                Step {currentStep} of 2{currentStep === 2 ? ' · Optional' : ''}
              </DialogDescription>
            </div>
          </div>
          {/* Progress Bar */}
          <div className="mt-3 flex gap-2">
            {[1, 2].map((step) => (
              <div
                key={step}
                className={cn(
                  'h-1 flex-1 rounded-full transition-colors',
                  step <= currentStep ? 'bg-primary' : 'bg-muted',
                )}
              />
            ))}
          </div>
        </DialogHeader>

        <div className="max-h-[55vh] overflow-y-auto pr-1">
          {/* Step 1: Select Ad Accounts */}
          {currentStep === 1 && (
            <div>
              {loadingCustomers ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Loader2 className="mb-3 size-8 animate-spin text-foreground" />
                  <p className="text-sm">Loading Google Ads accounts...</p>
                </div>
              ) : fetchError ? (
                <div className="py-8 text-center">
                  <p className="mb-2 text-sm text-destructive">{fetchError}</p>
                  <button
                    type="button"
                    onClick={onClose}
                    className="text-sm font-medium text-foreground hover:text-muted-foreground"
                  >
                    Close
                  </button>
                </div>
              ) : customers.length === 0 ? (
                <p className="rounded-lg bg-muted/50 py-4 text-center text-sm text-muted-foreground">
                  No Google Ads accounts found
                </p>
              ) : (
                <>
                  {/* Manager accounts with their children */}
                  {managerCustomers.length > 0 && (
                    <div className="mb-4">
                      {managerCustomers.map((mc) => {
                        const childAccounts = childCustomersByParent.get(mc.customerId) || [];
                        const mccStatus = getStatusBadge(mc.accountStatus);
                        return (
                          <div key={mc.customerId} className="mb-3">
                            <div className="rounded-lg border border-border bg-muted/50 p-3">
                              <div className="mb-1 flex items-center gap-2">
                                <p className="text-xs font-medium">Manager Account (MCC)</p>
                                {mccStatus && mccStatus.label !== 'Active' && (
                                  <Badge
                                    variant={mccStatus.variant}
                                    className="px-1.5 py-0 text-[10px]"
                                  >
                                    {mccStatus.label}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-sm">
                                {mc.customerName} · {formatCustomerId(mc.customerId)}
                              </p>
                              {childAccounts.length > 0 && (
                                <p className="mt-1 text-xs text-muted-foreground">
                                  {childAccounts.length} linked account
                                  {childAccounts.length !== 1 ? 's' : ''}
                                </p>
                              )}
                            </div>
                            {childAccounts.length > 0 && (
                              <div className="mt-2 space-y-2">
                                {childAccounts.map((child) => renderCustomerRow(child, true))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Top-level (standalone) non-manager accounts */}
                  {topLevelCustomers.length > 0 && (
                    <>
                      <div className="mb-3 flex items-center justify-between">
                        <h3 className="text-sm font-semibold">
                          {managerCustomers.length > 0 ? 'Non-Manager Accounts' : 'Ad Accounts'}{' '}
                          ({topLevelCustomers.length})
                        </h3>
                        {nonManagerCustomers.length > 0 && (
                          <button
                            type="button"
                            onClick={handleSelectAllCustomers}
                            className="text-xs font-medium text-foreground hover:text-muted-foreground"
                          >
                            {selectedCustomerIds.length === nonManagerCustomers.length
                              ? 'Deselect All'
                              : 'Select All'}
                          </button>
                        )}
                      </div>
                      <div className="space-y-2">
                        {topLevelCustomers.map((customer) => renderCustomerRow(customer))}
                      </div>
                    </>
                  )}

                  {/* If there are no top-level but there are child accounts, show Select All here */}
                  {topLevelCustomers.length === 0 && nonManagerCustomers.length > 0 && (
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="text-sm font-semibold">
                        Ad Accounts ({nonManagerCustomers.length})
                      </h3>
                      <button
                        type="button"
                        onClick={handleSelectAllCustomers}
                        className="text-xs font-medium text-foreground hover:text-muted-foreground"
                      >
                        {selectedCustomerIds.length === nonManagerCustomers.length
                          ? 'Deselect All'
                          : 'Select All'}
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Step 2: Link Merchant Center */}
          {currentStep === 2 && (
            <div>
              {loadingMerchants ? (
                <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                  <Loader2 className="mb-3 size-8 animate-spin text-foreground" />
                  <p className="text-sm">Checking linked Merchant Center accounts...</p>
                </div>
              ) : (
                <div>
                  <p className="mb-4 text-sm text-muted-foreground">
                    Link the Merchant Center account used by each Google Ads account. This step is
                    optional — you can skip it.
                  </p>
                  {merchantScopeError && (
                    <div className="mb-4 rounded-lg border border-border bg-muted/50 p-3">
                      <p className="text-sm text-muted-foreground">
                        Merchant Center linking is unavailable — re-connect with the content scope
                        to enable it. You can continue — this step is optional.
                      </p>
                    </div>
                  )}
                  <div className="space-y-3">
                    {selectedCustomerIds.map((customerId) => renderMerchantLink(customerId))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {saveError && <p className="text-sm text-destructive">{saveError}</p>}

        {/* Footer */}
        {!loadingCustomers && !fetchError && (
          <div className="flex items-center justify-between gap-2 border-t border-border pt-3">
            <p className="text-sm text-muted-foreground">{getSelectionCount()}</p>
            <div className="flex gap-2">
              {currentStep === 1 ? (
                <Button variant="outline" onClick={onClose} disabled={saving}>
                  Cancel
                </Button>
              ) : (
                <Button variant="outline" onClick={handleBack} disabled={saving}>
                  Back
                </Button>
              )}

              {currentStep === 1 && (
                <Button onClick={handleNext} disabled={!canNext() || saving}>
                  Next
                </Button>
              )}
              {currentStep === 2 && (
                <>
                  <Button variant="outline" onClick={handleSkipMerchantCenter} disabled={saving}>
                    Skip
                  </Button>
                  <Button onClick={handleConfirm} disabled={saving}>
                    {saving ? (
                      <>
                        <Loader2 className="size-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Confirm'
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
